import React, { useCallback, useEffect, useState } from 'react';
import { FormProvider } from 'react-hook-form';
import { useHistory } from 'react-router-dom';
import toast from 'react-hot-toast';
import { collection, doc, Timestamp } from 'firebase/firestore';
import styled from 'styled-components';
import { useForm } from 'hooks';
import { Button, Input, SelectForm } from 'shared/Components';
import { ColorField } from 'shared/Components';
import { FlexContainer } from 'shared/Typhography';
import { firestore, uploadThemeImage, setThemeData, buildThemeImageUrl } from 'services';

import UploadThemeImage from '../UploadThemeImage/UploadThemeImage';
import ThemePreview from '../ThemePreview/ThemePreview';
import { GameplayThemeData, getNextThemeOrder } from '../../../../../../services/api/themesApi';

import { ThemeFormField, themeFormSchema, REPEAT_MODE_OPTIONS } from './ThemeFormSchema';

const FormContainer = styled.form`
  margin-top: 32px;
`;

const Section = styled.div`
  margin-bottom: 32px;
`;

const SectionTitle = styled.div`
  font-size: 14px;
  font-weight: 700;
  color: #444;
  margin-bottom: 16px;
  padding-bottom: 6px;
  border-bottom: 1px solid #eee;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const ColorGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 12px 24px;
`;

const ColorLabel = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: #666;
  margin-bottom: 4px;
`;

const PreviewStickyWrapper = styled.div`
  position: sticky;
  top: 80px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
`;

const PreviewLabel = styled.div`
  font-size: 13px;
  font-weight: 700;
  color: #555;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const TwoColumnLayout = styled.div`
  display: flex;
  gap: 40px;
  align-items: flex-start;

  @media (max-width: 900px) {
    flex-direction: column;
  }
`;

const FormColumn = styled.div`
  flex: 1;
  min-width: 0;
`;

interface ThemeFormProps {
  existingTheme?: GameplayThemeData | null;
  themeId?: string;
}

const ThemeForm: React.FC<ThemeFormProps> = ({ existingTheme, themeId }) => {
  const isCreate = !themeId;
  const history = useHistory();
  const [isSaving, setIsSaving] = useState(false);
  const [nextOrder, setNextOrder] = useState<number>(0);

  // Raw File state outside form — same pattern as GameEventForm
  const [bgFile, setBgFile] = useState<File | null>(null);
  const [boardFile, setBoardFile] = useState<File | null>(null);
  const [previewFile, setPreviewFile] = useState<File | null>(null);

  // Explicit removal flags — needed to distinguish "user deleted existing image"
  // from "user cancelled a new file selection" (both call onFileChange(null)).
  const [bgRemoved, setBgRemoved] = useState(false);
  const [boardRemoved, setBoardRemoved] = useState(false);
  const [previewRemoved, setPreviewRemoved] = useState(false);

  const defaultRepeatMode = existingTheme?.gameplayBackgroundRepeatMode
    ? REPEAT_MODE_OPTIONS.find((o) => o.value === existingTheme.gameplayBackgroundRepeatMode) ??
      REPEAT_MODE_OPTIONS[0]
    : null;

  const { control, formState, handleSubmit, watch, setValue, ...methods } = useForm<ThemeFormField>(
    {
      schema: themeFormSchema,
      defaultValues: {
        name: existingTheme?.name ?? '',
        order: existingTheme?.order ?? 0,
        scrollerColor: existingTheme?.scrollerColor ?? '#FF1A1A2E',
        buttonsBackgroundColor: existingTheme?.buttonsBackgroundColor ?? '#FF252535',
        buttonsIconsColor: existingTheme?.buttonsIconsColor ?? '#80FFFFFF',
        buttonsActiveIconsColor: existingTheme?.buttonsActiveIconsColor ?? '',
        gameplayBackgroundColor: existingTheme?.gameplayBackgroundColor ?? '',
        boardBackgroundColor: existingTheme?.boardBackgroundColor ?? '',
        gameplayBackgroundRepeatMode: defaultRepeatMode as any,
        gameplayBackgroundImageUrl: existingTheme?.gameplayBackgroundImageUrl ?? '',
        boardBackgroundImageUrl: existingTheme?.boardBackgroundImageUrl ?? '',
        previewUrl: existingTheme?.previewUrl ?? '',
      },
    },
  );

  useEffect(() => {
    if (isCreate) {
      getNextThemeOrder().then((n) => {
        setNextOrder(n);
        setValue('order', n);
      });
    }
  }, [isCreate, setValue]);

  const watched = watch();

  const onBgFileChange = useCallback(
    (file: File | null) => {
      setBgFile(file);
      if (file) {
        setBgRemoved(false);
        setValue('gameplayBackgroundImageUrl', URL.createObjectURL(file));
      } else {
        setBgRemoved(true);
        setValue('gameplayBackgroundImageUrl', '');
      }
    },
    [setValue],
  );

  const onBoardFileChange = useCallback(
    (file: File | null) => {
      setBoardFile(file);
      if (file) {
        setBoardRemoved(false);
        setValue('boardBackgroundImageUrl', URL.createObjectURL(file));
      } else {
        setBoardRemoved(true);
        setValue('boardBackgroundImageUrl', '');
      }
    },
    [setValue],
  );

  const onPreviewFileChange = useCallback(
    (file: File | null) => {
      setPreviewFile(file);
      if (file) {
        setPreviewRemoved(false);
        setValue('previewUrl', URL.createObjectURL(file));
      } else {
        setPreviewRemoved(true);
        setValue('previewUrl', '');
      }
    },
    [setValue],
  );

  const submit = handleSubmit(async (data) => {
    if (isSaving) return;
    setIsSaving(true);
    try {
      const themesRef = collection(firestore, 'gameplayThemes');
      const resolvedId = themeId ?? doc(themesRef).id;

      // Upload new images, keep existing ones, or clear removed ones.
      let bgUrl = bgRemoved ? null : existingTheme?.gameplayBackgroundImageUrl ?? null;
      let boardUrl = boardRemoved ? null : existingTheme?.boardBackgroundImageUrl ?? null;
      let previewUrl = previewRemoved ? null : existingTheme?.previewUrl ?? null;

      if (bgFile) {
        const result = await uploadThemeImage(resolvedId, bgFile, 'background');
        bgUrl = buildThemeImageUrl(result.ref.fullPath);
      }
      if (boardFile) {
        const result = await uploadThemeImage(resolvedId, boardFile, 'board');
        boardUrl = buildThemeImageUrl(result.ref.fullPath);
      }
      if (previewFile) {
        const result = await uploadThemeImage(resolvedId, previewFile, 'preview');
        previewUrl = buildThemeImageUrl(result.ref.fullPath);
      }

      const payload = {
        name: data.name.trim(),
        createdAt: existingTheme ? (existingTheme.createdAt as any) : Timestamp.now(),
        order: data.order,
        scrollerColor: data.scrollerColor,
        buttonsBackgroundColor: data.buttonsBackgroundColor,
        buttonsIconsColor: data.buttonsIconsColor,
        buttonsActiveIconsColor: data.buttonsActiveIconsColor || null,
        gameplayBackgroundColor: data.gameplayBackgroundColor || null,
        boardBackgroundColor: data.boardBackgroundColor || null,
        gameplayBackgroundRepeatMode: (data.gameplayBackgroundRepeatMode as any)?.value ?? null,
        gameplayBackgroundImageUrl: bgUrl,
        boardBackgroundImageUrl: boardUrl,
        previewUrl: previewUrl,
      };

      await setThemeData(resolvedId, payload as any, isCreate);
      toast.success(isCreate ? 'Theme created!' : 'Theme updated!');
      history.push('/themes');
    } catch (err) {
      console.error(err);
      toast.error('Failed to save theme. Please try again.');
    } finally {
      setIsSaving(false);
    }
  });

  const errors = formState.errors;

  return (
    <FormProvider
      control={control}
      formState={formState}
      handleSubmit={handleSubmit}
      watch={watch}
      setValue={setValue}
      {...methods}
    >
      <TwoColumnLayout>
        <FormColumn>
          <FormContainer onSubmit={submit}>
            {/* Name */}
            <Section>
              <SectionTitle>General</SectionTitle>
              <Input
                required
                placeholder="Theme name"
                name="name"
                type="text"
                control={control}
                error={errors['name']}
                disabled={isSaving}
                defaultValue={existingTheme?.name ?? ''}
                outerWidth="100%"
                margin="0 0 8px 0"
              />
              <Input
                required
                placeholder="Display order (lower = first)"
                name="order"
                type="number"
                control={control}
                error={errors['order']}
                disabled={isSaving}
                defaultValue={String(existingTheme?.order ?? nextOrder)}
                outerWidth="220px"
                margin="0 0 8px 0"
              />
            </Section>

            {/* Required Colors */}
            <Section>
              <SectionTitle>Required Colors</SectionTitle>
              <ColorGrid>
                <div>
                  <ColorLabel>Scroller Color *</ColorLabel>
                  <ColorField
                    name="scrollerColor"
                    control={control}
                    disabled={isSaving}
                    error={errors['scrollerColor']}
                  />
                </div>
                <div>
                  <ColorLabel>Buttons Background Color *</ColorLabel>
                  <ColorField
                    name="buttonsBackgroundColor"
                    control={control}
                    disabled={isSaving}
                    error={errors['buttonsBackgroundColor']}
                  />
                </div>
                <div>
                  <ColorLabel>Buttons Icons Color *</ColorLabel>
                  <ColorField
                    name="buttonsIconsColor"
                    control={control}
                    disabled={isSaving}
                    error={errors['buttonsIconsColor']}
                  />
                </div>
                <div>
                  <ColorLabel>Buttons Active Icons Color</ColorLabel>
                  <ColorField
                    name="buttonsActiveIconsColor"
                    control={control}
                    disabled={isSaving}
                    clearable
                    error={errors['buttonsActiveIconsColor']}
                    defaultValue=""
                  />
                </div>
              </ColorGrid>
            </Section>

            {/* Background */}
            <Section>
              <SectionTitle>Gameplay Background</SectionTitle>
              <ColorGrid>
                <div>
                  <ColorLabel>Background Color</ColorLabel>
                  <ColorField
                    name="gameplayBackgroundColor"
                    control={control}
                    disabled={isSaving}
                    clearable
                    error={errors['gameplayBackgroundColor']}
                    defaultValue=""
                  />
                </div>
              </ColorGrid>
              <div style={{ marginTop: 12, maxWidth: 240 }}>
                <SelectForm
                  placeholder="Background repeat mode"
                  name="gameplayBackgroundRepeatMode"
                  control={control}
                  disabled={isSaving}
                  values={REPEAT_MODE_OPTIONS}
                  error={errors['gameplayBackgroundRepeatMode']}
                  defaultValue={defaultRepeatMode}
                  width="240px"
                />
              </div>
              <div style={{ marginTop: 16, maxWidth: 300 }}>
                <UploadThemeImage
                  label="Background Image"
                  imageUrl={watched.gameplayBackgroundImageUrl}
                  onFileChange={onBgFileChange}
                  disabled={isSaving}
                />
              </div>
            </Section>

            {/* Board */}
            <Section>
              <SectionTitle>Board Background</SectionTitle>
              <ColorGrid>
                <div>
                  <ColorLabel>Board Background Color</ColorLabel>
                  <ColorField
                    name="boardBackgroundColor"
                    control={control}
                    disabled={isSaving}
                    clearable
                    error={errors['boardBackgroundColor']}
                    defaultValue=""
                  />
                </div>
              </ColorGrid>
              <div style={{ marginTop: 16, maxWidth: 300 }}>
                <UploadThemeImage
                  label="Board Image"
                  imageUrl={watched.boardBackgroundImageUrl}
                  onFileChange={onBoardFileChange}
                  disabled={isSaving}
                />
              </div>
            </Section>

            {/* Preview image */}
            <Section>
              <SectionTitle>Theme Preview Image</SectionTitle>
              <div style={{ color: '#888', fontSize: 12, marginBottom: 12 }}>
                Optional full-tile cover image shown in the app&apos;s theme picker instead of the
                composed preview.
              </div>
              <div style={{ maxWidth: 300 }}>
                <UploadThemeImage
                  label="Preview Image"
                  imageUrl={watched.previewUrl}
                  onFileChange={onPreviewFileChange}
                  disabled={isSaving}
                />
              </div>
            </Section>

            {/* Actions */}
            <FlexContainer gap="16px" margin="32px 0 40px">
              <Button
                variant="danger"
                height={40}
                style={{ minWidth: 140 }}
                disabled={isSaving}
                onClick={() => history.push('/themes')}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                height={40}
                style={{ minWidth: 180 }}
                disabled={isSaving}
                onClick={submit}
              >
                {isSaving ? 'Saving…' : isCreate ? 'Create Theme' : 'Save Changes'}
              </Button>
            </FlexContainer>
          </FormContainer>
        </FormColumn>

        {/* Live preview */}
        <div style={{ paddingTop: 32 }}>
          <PreviewStickyWrapper>
            <PreviewLabel>Live Preview</PreviewLabel>
            <ThemePreview
              gameplayBackgroundColor={watched.gameplayBackgroundColor}
              gameplayBackgroundImageUrl={watched.gameplayBackgroundImageUrl}
              boardBackgroundColor={watched.boardBackgroundColor}
              boardBackgroundImageUrl={watched.boardBackgroundImageUrl}
              scrollerColor={watched.scrollerColor}
              buttonsBackgroundColor={watched.buttonsBackgroundColor}
              buttonsIconsColor={watched.buttonsIconsColor}
              buttonsActiveIconsColor={watched.buttonsActiveIconsColor}
              gameplayBackgroundRepeatMode={watched.gameplayBackgroundRepeatMode as any}
            />
          </PreviewStickyWrapper>
        </div>
      </TwoColumnLayout>
    </FormProvider>
  );
};

export default ThemeForm;
